import { useMemo, type ReactNode } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlertTriangleIcon, InfoIcon, RefreshCwIcon } from "lucide-react";
import { formatCurrency, formatNumber } from "@dolfin/lib";
import { InfoHint } from "./InfoHint";

// Three-way match statistics for portal and admin; both scopes return the same shape. Plain token-classed divs,
// not shadcn, per the shared-component rule.

export type MatchStatsPeriod = "Last30Days" | "Last90Days" | "MonthToDate";

// Structural mirror of MatchStatsResponse so this package needs no generated types; shape changes still fail at call sites.
export interface MatchStatsData {
  scope: string;
  clientId?: string | null;
  organisationId?: string | null;
  asOf: string;
  period: string;
  from: string;
  to: string;
  bills: { total: number; withMatchGroup: number; withoutMatchGroup: number };
  groups: {
    total: number;
    open: number;
    variance: number;
    resolved: number;
    straightThrough: number;
    cleanFirstPass: number;
    straightThroughRate?: number | null;
    cleanFirstPassRate?: number | null;
  };
  lines: {
    total: number;
    matched: { autoExact: number; autoFuzzy: number; assisted: number; manual: number; total: number };
    unmatched: {
      acceptedNoMatch: number;
      disputed: number;
      open: number;
      candidateWaiting: number;
      noCandidate: number;
      total: number;
    };
  };
  rates: {
    automationOfMatched?: number | null;
    assist?: number | null;
    manual?: number | null;
    grossCoverage?: number | null;
    disposition?: number | null;
    automation?: number | null;
    matchCoverage?: number | null;
    waiting?: number | null;
    noCounterpart?: number | null;
  };
  value: {
    currency: string;
    matched: number;
    automated: number;
    dispositioned: number;
    open: number;
    automationOfMatched?: number | null;
  }[];
  resolutions: {
    accept: number;
    acceptAndUpdateCatalog: number;
    requestCreditNote: number;
    dispute: number;
    autoSettled: number;
    humanSettled: number;
    total: number;
  };
  confidence: { from: number; to: number; autoAccepted: number; humanConfirmed: number }[];
  variance: {
    priceVariance: number;
    receiptQuantityVariance: number;
    overBilling: number;
    anyVariance: number;
    fullyAllocated: number;
    withSuggestion: number;
    averageTopConfidence?: number | null;
    oldestComputedAt?: string | null;
  };
  manualCauses: {
    total: number;
    noCandidate: number;
    noSupplier: number;
    routeDisabled: number;
    belowThreshold: number;
    ambiguous: number;
    eligible: number;
    unknown: number;
    averageEngineConfidence?: number | null;
    nearMiss: number;
    nearMissBand: number;
  };
  counterparts: {
    purchaseOrdersWithCapacity: number;
    purchaseOrdersUntouched: number;
    deliveryNotesWithCapacity: number;
    deliveryNotesUntouched: number;
  };
  settings: {
    autoAcceptThreshold: number;
    similarityThreshold: number;
    roundingTolerance: number;
    poAutoMatchEnabled: boolean;
    dnAutoMatchEnabled: boolean;
  };
}

export interface MatchStatsViewProps {
  data?: MatchStatsData | null;
  loading?: boolean;
  error?: string | null;
  period: MatchStatsPeriod;
  onPeriodChange: (period: MatchStatsPeriod) => void;
  onRetry?: () => void;
  /** Extra header controls (admin's cache refresh), so that button stays out of the customer build entirely. */
  actions?: ReactNode;
  /** Tightens spacing for the admin console, which runs denser than the portal. */
  dense?: boolean;
  /** Admin only: numerator and denominator under each rate, since a rate can rest on a tiny denominator
   * (straight-through is a share of settled bills only). */
  showCounts?: boolean;
}

const PERIODS: { id: MatchStatsPeriod; label: string }[] = [
  { id: "Last30Days", label: "30 days" },
  { id: "Last90Days", label: "90 days" },
  { id: "MonthToDate", label: "Month to date" },
];

// Categorical palette slots 1-4, fixed order, validated in both modes. Light mode warns on aqua and yellow contrast,
// hence direct labels and the value table: identity never rests on colour alone.
const VIZ_STYLES = `
.dolfin-viz {
  --viz-surface: #fcfcfb;
  --series-1: #2a78d6;
  --series-2: #eb6834;
  --series-3: #1baf7a;
  --series-4: #eda100;
}
.dark .dolfin-viz {
  --viz-surface: #1a1a19;
  --series-1: #3987e5;
  --series-2: #d95926;
  --series-3: #199e70;
  --series-4: #c98500;
}
`;

// Ordered least-to-most human, so the bar reads left to right as reviewer effort increasing.
const MATCH_SERIES = [
  { key: "autoExact", label: "Auto - SKU", fill: "var(--series-1)" },
  { key: "autoFuzzy", label: "Auto - similarity", fill: "var(--series-2)" },
  { key: "assisted", label: "Confirmed", fill: "var(--series-3)" },
  { key: "manual", label: "Manual", fill: "var(--series-4)" },
] as const;

type MatchSeriesKey = (typeof MATCH_SERIES)[number]["key"];

function percent(value: number | null | undefined): string {
  if (value === null || value === undefined) return "n/a";
  return `${(value * 100).toFixed(1)}%`;
}

const TOOLTIP_STYLE = {
  background: "var(--color-popover, #fff)",
  border: "1px solid var(--color-border, #e5e7eb)",
  borderRadius: 8,
  fontSize: 12,
  color: "var(--color-popover-foreground, #111)",
} as const;

export function MatchStatsView({
  data,
  loading = false,
  error = null,
  period,
  onPeriodChange,
  onRetry,
  actions,
  dense = false,
  showCounts = false,
}: MatchStatsViewProps) {
  const gap = dense ? "gap-4" : "gap-6";

  const matchedCounts = useMemo<Record<MatchSeriesKey, number>>(() => {
    const m = data?.lines.matched;
    return {
      autoExact: m?.autoExact ?? 0,
      autoFuzzy: m?.autoFuzzy ?? 0,
      assisted: m?.assisted ?? 0,
      manual: m?.manual ?? 0,
    };
  }, [data]);

  const confidenceBars = useMemo(
    () =>
      (data?.confidence ?? []).map((b) => ({
        band: `${b.from.toFixed(1)}-${b.to.toFixed(1)}`,
        Automatic: b.autoAccepted,
        Confirmed: b.humanConfirmed,
      })),
    [data],
  );

  if (loading && !data) {
    return (
      <div className={`flex flex-col ${gap}`}>
        <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
        <div className="h-40 animate-pulse rounded-lg bg-muted" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-6 py-10 text-center">
        <AlertTriangleIcon className="size-6 text-destructive" />
        <p className="text-sm font-medium">Could not load match stats</p>
        <p className="max-w-md text-sm text-muted-foreground">{error}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-1 inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm font-medium transition hover:bg-accent"
          >
            <RefreshCwIcon className="size-3.5" />
            Try again
          </button>
        )}
      </div>
    );
  }

  const headerControls = (
    <div className="inline-flex flex-wrap items-center gap-2">
      <div className="inline-flex rounded-md border border-border p-0.5">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onPeriodChange(p.id)}
            className={`rounded px-2.5 py-1 text-xs font-medium transition ${
              period === p.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {actions}
    </div>
  );

  if (!data || data.lines.total === 0) {
    return (
      <div className={`flex flex-col ${gap}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-medium text-muted-foreground">Three-way matching</h3>
          {headerControls}
        </div>
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border px-6 py-12 text-center">
          <InfoIcon className="size-6 text-muted-foreground" />
          <p className="text-sm font-medium">No matching activity in this period</p>
          <p className="max-w-md text-sm text-muted-foreground">
            Match groups open when a bill arrives for a supplier that has an open purchase order or delivery
            note. Try a longer period, or check that purchase orders are being loaded.
          </p>
        </div>
      </div>
    );
  }

  const { lines, groups, rates, value, resolutions, settings, counterparts, variance, manualCauses } = data;
  const waiting = lines.unmatched.candidateWaiting;
  const starved = lines.unmatched.noCandidate;

  return (
    <div className={`dolfin-viz flex flex-col ${gap}`}>
      <style>{VIZ_STYLES}</style>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium">Three-way matching</h3>
          <p className="text-xs text-muted-foreground">
            {data.from} to {data.to} - updated {new Date(data.asOf).toLocaleTimeString()}
          </p>
        </div>
        {headerControls}
      </div>

      {/* Automation is over addressable lines (matched + waiting), so unmatchable lines don't drag it down. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi
          label="Automated"
          value={percent(rates.automation ?? rates.automationOfMatched)}
          hint="Share of matchable lines the engine matched with no reviewer"
          note={
            showCounts
              ? `${formatNumber(lines.matched.autoExact + lines.matched.autoFuzzy)} of ${formatNumber(
                  lines.matched.total + waiting,
                )} addressable lines`
              : undefined
          }
          accent
        />
        <Kpi
          label="Waiting"
          value={formatNumber(waiting)}
          hint="Unmatched lines whose supplier still has order or delivery capacity - someone can act on these now"
          note={showCounts ? `of ${formatNumber(lines.total)} lines` : undefined}
          tone={waiting > 0 ? "warn" : "default"}
        />
        <Kpi
          label="Straight through"
          value={percent(groups.straightThroughRate)}
          hint="Settled bills that never needed a human at all"
          note={
            showCounts
              ? `${formatNumber(groups.straightThrough)} of ${formatNumber(groups.resolved)} settled` +
                ` - ${formatNumber(groups.total)} bills in total`
              : undefined
          }
        />
        <Kpi
          label="Awaiting a decision"
          value={formatNumber(groups.variance)}
          hint="Match groups with a flagged difference - these block the bill from leaving review"
          note={showCounts ? `of ${formatNumber(groups.total)} bills` : undefined}
          tone={groups.variance > 0 ? "warn" : "default"}
        />
      </div>

      {/* A CSS proportion bar, each segment labelled with its count: light mode warns on two slots' contrast. */}
      {lines.matched.total > 0 && (
        <Panel
          title={`How ${formatNumber(lines.matched.total)} matched lines were matched`}
          meta={`${formatNumber(lines.total)} lines across ${formatNumber(groups.total)} bills`}
        >
          <div className="flex h-10 w-full overflow-hidden rounded" role="img"
            aria-label={MATCH_SERIES.map((s) => `${s.label} ${matchedCounts[s.key]}`).join(", ")}>
            {MATCH_SERIES.map((s, i) => {
              const count = matchedCounts[s.key];
              if (count === 0) return null;
              const share = count / lines.matched.total;
              return (
                <div
                  key={s.key}
                  className="flex items-center justify-center overflow-hidden"
                  style={{
                    width: `${share * 100}%`,
                    background: s.fill,
                    marginLeft: i === 0 ? 0 : 2,
                  }}
                  title={`${s.label}: ${formatNumber(count)} (${(share * 100).toFixed(1)}%)`}
                >
                  {share > 0.07 && (
                    <span className="px-1 text-[11px] font-medium text-white tabular-nums">
                      {formatNumber(count)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
            {MATCH_SERIES.map((s) => (
              <li key={s.key} className="flex items-center gap-1.5 text-xs">
                <span className="size-2.5 shrink-0 rounded-full" style={{ background: s.fill }} aria-hidden />
                <span className="text-muted-foreground">{s.label}</span>
                <span className="font-medium tabular-nums">{formatNumber(matchedCounts[s.key])}</span>
                <span className="text-muted-foreground tabular-nums">
                  {((matchedCounts[s.key] / lines.matched.total) * 100).toFixed(1)}%
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {/* The question the whole surface exists to answer: of what is not matched, what can be acted on. */}
      <Panel title="What is not matched" meta={`${formatNumber(lines.unmatched.total)} lines`}>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            label="Can be matched now"
            value={formatNumber(waiting)}
            note="Supplier has capacity left"
            tone={waiting > 0 ? "warn" : "default"}
          />
          <Stat
            label="Nothing to match to"
            value={formatNumber(starved)}
            note="No order or delivery left - accept or chase a document"
          />
          <Stat
            label="Accepted as billed"
            value={formatNumber(lines.unmatched.acceptedNoMatch)}
            note="Closed with no counterpart"
          />
          <Stat
            label="Disputed"
            value={formatNumber(lines.unmatched.disputed)}
            note="Raised with the supplier"
          />
        </div>
      </Panel>

      {/* Read from the persisted per-line snapshot rather than re-derived. */}
      {variance.anyVariance > 0 && (
        <Panel
          title="What kind of difference"
          meta={`${formatNumber(variance.anyVariance)} lines flagged`}
        >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Billed above the order" value={formatNumber(variance.priceVariance)} note="Unit price differs" />
            <Stat
              label="Quantity differs from delivery"
              value={formatNumber(variance.receiptQuantityVariance)}
              note="Billed against received"
            />
            <Stat
              label="Billed beyond the order"
              value={formatNumber(variance.overBilling)}
              note="More than the order authorises"
            />
            <Stat
              label="Fully covered"
              value={formatNumber(variance.fullyAllocated)}
              note="Net matched within tolerance"
            />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            A line with more than one kind of difference is counted once in the total and under each kind it
            has, so the four figures above can add up to more than the total.
          </p>
        </Panel>
      )}

      {/* Causes kept separate: a threshold cut recovers below-threshold lines and does nothing for ambiguous ones. */}
      {manualCauses.total > 0 && (
        <Panel
          title="Why a human was needed"
          meta={`${formatNumber(manualCauses.total)} lines a reviewer matched`}
        >
          <ManualCauseBars causes={manualCauses} />
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-border/70 p-3">
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                Within {percent(manualCauses.nearMissBand)} of the bar
                <InfoHint label="Near misses">
                  Lines the engine scored just below the auto-accept threshold of{" "}
                  {percent(settings.autoAcceptThreshold)}. Roughly what a threshold cut of that size would stop
                  putting in front of a reviewer &mdash; roughly, because a lower bar also admits matches that
                  are wrong. It sizes the prize; it does not argue for taking it.
                </InfoHint>
              </p>
              <p className="mt-1 text-xl font-semibold tabular-nums">{formatNumber(manualCauses.nearMiss)}</p>
            </div>
            <div className="rounded-md border border-border/70 p-3">
              <p className="text-xs text-muted-foreground">Average engine confidence</p>
              <p className="mt-1 text-xl font-semibold tabular-nums">
                {percent(manualCauses.averageEngineConfidence)}
              </p>
              <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                Across the lines it scored at all, against a bar of {percent(settings.autoAcceptThreshold)}
              </p>
            </div>
          </div>
          {manualCauses.unknown > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              {formatNumber(manualCauses.unknown)} of these were matched before the engine&rsquo;s position was
              recorded, so no cause is known for them. Shown rather than folded into a real cause &mdash;
              missing data should not read as a finding.
            </p>
          )}
        </Panel>
      )}

      {/* Not the backlog count: suggestions score against a pool other bills can consume. */}
      {variance.withSuggestion > 0 && (
        <Panel title="Near misses" meta="Open lines the engine had a candidate for">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-border/70 p-3">
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                Open lines with a candidate
                <InfoHint label="Open lines with a candidate">
                  A quality signal, not a work queue. Candidates are scored against orders and deliveries that
                  another bill can consume without this line being recalculated, so this can overstate. Use
                  &ldquo;Can be matched now&rdquo; to decide what is actionable.
                </InfoHint>
              </p>
              <p className="mt-1 text-xl font-semibold tabular-nums">{formatNumber(variance.withSuggestion)}</p>
            </div>
            <div className="rounded-md border border-border/70 p-3">
              <p className="text-xs text-muted-foreground">Average best confidence</p>
              <p className="mt-1 text-xl font-semibold tabular-nums">{percent(variance.averageTopConfidence)}</p>
              <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                Against an auto-accept bar of {percent(settings.autoAcceptThreshold)}
              </p>
            </div>
          </div>
        </Panel>
      )}

      {/* The supply side: "the engine is doing nothing" often means no orders loaded or no bills arriving. */}
      <Panel title="Orders and deliveries" meta="Documents with quantity still unclaimed">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Orders with capacity" value={formatNumber(counterparts.purchaseOrdersWithCapacity)} />
          <Stat
            label="Orders never matched"
            value={formatNumber(counterparts.purchaseOrdersUntouched)}
            note="Loaded, but nothing has matched them"
            tone={counterparts.purchaseOrdersUntouched > 0 ? "warn" : "default"}
          />
          <Stat label="Deliveries with capacity" value={formatNumber(counterparts.deliveryNotesWithCapacity)} />
          <Stat
            label="Deliveries never matched"
            value={formatNumber(counterparts.deliveryNotesUntouched)}
            tone={counterparts.deliveryNotesUntouched > 0 ? "warn" : "default"}
          />
        </div>
      </Panel>

      {/* Confidence calibration - the diagnostic that says whether the auto-accept threshold is set right. */}
      {confidenceBars.some((b) => b.Automatic + b.Confirmed > 0) && (
        <Panel
          title="Similarity match confidence"
          meta={`auto-accept at ${percent(settings.autoAcceptThreshold)}`}
        >
          <p className="mb-3 text-xs text-muted-foreground">
            A tall confirmed bar just below the threshold means reviewers are agreeing with the engine anyway -
            the threshold could come down.
          </p>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={confidenceBars} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border, #e5e7eb)" />
                <XAxis dataKey="band" tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground, #6b7280)" />
                <YAxis tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground, #6b7280)" allowDecimals={false} />
                <Tooltip cursor={{ fill: "var(--color-accent, #f3f4f6)", opacity: 0.4 }} contentStyle={TOOLTIP_STYLE} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Automatic" stackId="c" fill="var(--series-1)" stroke="var(--viz-surface)" strokeWidth={2} />
                <Bar dataKey="Confirmed" stackId="c" fill="var(--series-3)" stroke="var(--viz-surface)" strokeWidth={2} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      )}

      {/* Never summed across currencies. Doubles as the table view the light-mode contrast warning requires. */}
      {value.length > 0 && (
        <Panel title="Billed value matched" meta="Per currency - never summed">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="pb-2 font-medium">Currency</th>
                  <th className="pb-2 text-right font-medium">Matched</th>
                  <th className="pb-2 text-right font-medium">Automated</th>
                  <th className="pb-2 text-right font-medium">Open</th>
                  <th className="pb-2 text-right font-medium">Automated share</th>
                </tr>
              </thead>
              <tbody>
                {value.map((v) => (
                  <tr key={v.currency} className="border-b border-border/50 last:border-0">
                    <td className="py-2 font-medium">{v.currency}</td>
                    <td className="py-2 text-right tabular-nums">{formatCurrency(v.matched, v.currency)}</td>
                    <td className="py-2 text-right tabular-nums">{formatCurrency(v.automated, v.currency)}</td>
                    <td className="py-2 text-right tabular-nums">{formatCurrency(v.open, v.currency)}</td>
                    <td className="py-2 text-right tabular-nums">{percent(v.automationOfMatched)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {resolutions.total > 0 && (
        <p className="text-xs text-muted-foreground">
          {formatNumber(resolutions.total)} line decisions recorded - {formatNumber(resolutions.autoSettled)}{" "}
          settled automatically, {formatNumber(resolutions.humanSettled)} by a reviewer. Shows what stands now,
          not a history.
        </p>
      )}
    </div>
  );
}

// Ordered by what a reader can do: threshold causes first, then document/setting ones, then the should-never-happen one.
const MANUAL_CAUSES = [
  { key: "belowThreshold", label: "Under the threshold", fill: "var(--series-2)" },
  { key: "ambiguous", label: "Too close to call", fill: "var(--series-4)" },
  { key: "noCandidate", label: "Nothing to match", fill: "var(--series-1)" },
  { key: "noSupplier", label: "No supplier", fill: "var(--series-3)" },
  { key: "routeDisabled", label: "Auto-match off", fill: "var(--color-muted-foreground, #6b7280)" },
  { key: "eligible", label: "Should have matched", fill: "var(--color-destructive, #dc2626)" },
] as const;

function ManualCauseBars({ causes }: { causes: MatchStatsData["manualCauses"] }) {
  const rows = MANUAL_CAUSES.map((c) => ({ ...c, count: causes[c.key] })).filter((c) => c.count > 0);
  const max = Math.max(1, ...rows.map((r) => r.count));

  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No cause was recorded for any of these matches yet.</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      {rows.map((row) => (
        <div key={row.key} className="flex items-center gap-3">
          <span className="w-44 shrink-0 text-xs text-muted-foreground">{row.label}</span>
          <div className="h-2.5 flex-1 rounded-full bg-muted">
            {/* Scaled to the largest cause, not the total, so small causes don't flatten to nothing. */}
            <div
              className="h-2.5 rounded-full"
              style={{ width: `${(row.count / max) * 100}%`, background: row.fill }}
            />
          </div>
          <span className="w-10 shrink-0 text-right text-xs font-medium tabular-nums">
            {formatNumber(row.count)}
          </span>
        </div>
      ))}
    </div>
  );
}

function Panel({ title, meta, children }: { title: string; meta?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-border p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="text-sm font-medium">{title}</h4>
        {meta && <span className="text-xs text-muted-foreground">{meta}</span>}
      </div>
      {children}
    </section>
  );
}

function Kpi({
  label,
  value,
  hint,
  note,
  accent = false,
  tone = "default",
}: {
  label: string;
  value: string;
  hint: string;
  /** The counts behind `value`. Admin only - see `showCounts` on MatchStatsViewProps. */
  note?: string;
  accent?: boolean;
  tone?: "default" | "warn";
}) {
  return (
    <div className={`rounded-lg border p-3 ${accent ? "border-border bg-muted/40" : "border-border"}`}>
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {label}
        <InfoHint label={label}>{hint}</InfoHint>
      </p>
      <p
        className={`mt-1 tabular-nums font-semibold ${accent ? "text-3xl" : "text-2xl"} ${
          tone === "warn" ? "text-amber-600 dark:text-amber-500" : ""
        }`}
      >
        {value}
      </p>
      {note && <p className="mt-1 text-[11px] leading-snug tabular-nums text-muted-foreground">{note}</p>}
    </div>
  );
}

function Stat({
  label,
  value,
  note,
  tone = "default",
}: {
  label: string;
  value: string;
  note?: string;
  tone?: "default" | "warn";
}) {
  return (
    <div className="rounded-md border border-border/70 p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={`mt-1 text-xl font-semibold tabular-nums ${
          tone === "warn" ? "text-amber-600 dark:text-amber-500" : ""
        }`}
      >
        {value}
      </p>
      {note && <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{note}</p>}
    </div>
  );
}
